import { useEffect, useState } from "react";
import { api } from "../../../lib/api";

export type ContactField = {
  code: string;
  label: string;
  required: boolean;
  type: string;
  aliases: string[];
};

// Module-level cache — the field dictionary is immutable in Milestone D and
// callers should not re-fetch it per component mount.
let _cache: ContactField[] | null = null;
let _inflight: Promise<ContactField[]> | null = null;

async function fetchOnce(): Promise<ContactField[]> {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = api
    .get<{ fields: ContactField[] }>("/import/contact-fields")
    .then((r) => {
      _cache = r.data.fields || [];
      _inflight = null;
      return _cache;
    })
    .catch((e) => {
      _inflight = null;
      throw e;
    });
  return _inflight;
}

/** Test-only helper — resets the module cache between Vitest suites. */
export function __resetFieldDictionaryCache(): void {
  _cache = null;
  _inflight = null;
}

export function useFieldDictionary(): {
  fields: ContactField[] | null;
  error: unknown;
} {
  const [fields, setFields] = useState<ContactField[] | null>(_cache);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let cancelled = false;
    if (fields) return; // already resolved
    fetchOnce()
      .then((f) => {
        if (!cancelled) setFields(f);
      })
      .catch((e) => {
        if (!cancelled) setError(e);
      });
    return () => {
      cancelled = true;
    };
  }, [fields]);

  return { fields, error };
}
